/**
 * Bridge module: adapts errorhead WebGL2 GLSL effects to the
 * unstaglitch/pixeledit/framestack canvas ImageData format.
 *
 * Ported from eg0pr0xy/errorhead (https://github.com/eg0pr0xy/errorhead).
 * Original shaders in services/glitchEngine/webgl/shaders.ts;
 * Canvas2D effects in destruction.ts and post.ts ported to GLSL.
 *
 * This bridge follows the exact same pattern as openmosh-bridge.js:
 *   1. Creates a WebGL2 context on a separate off-DOM canvas
 *   2. Uploads the 2D canvas's ImageData as a texture
 *   3. Runs the effect shader
 *   4. Reads back the result and puts it on the 2D canvas
 *
 * Animated effects use u_time — we pass a random time value for variety
 * (same as openmosh-bridge for still-image processing).
 */

const {
    VERTEX_SHADER,
    EFFECT_SHADERS
} = require('./errorhead-shaders');

const { EFFECT_DEFINITIONS } = require('./errorhead-definitions');

// Reuse the same GL utilities as OpenMosh (identical WebGL2 helpers)
const { createProgram, getUniformLocations } = require('./openmosh-gl-utils');

// ---- Minimal WebGL2 renderer ----
// (Identical structure to openmosh-bridge's MiniGLRenderer, kept separate
// to avoid coupling between the two bridge modules' GL state.)

class ErrorheadGLRenderer {
    constructor() {
        this.gl = null;
        this.canvas = null;
        this.quadVAO = null;
        this.inputTexture = null;
        this.fbTexture = null;
        this.fbFBO = null;
        this.programs = new Map();  // effectId -> { program, uniforms }
        this.width = 0;
        this.height = 0;
    }

    _ensureGL(width, height) {
        if (this.gl && this.canvas) {
            if (this.canvas.width !== width) this.canvas.width = width;
            if (this.canvas.height !== height) this.canvas.height = height;
            this.width = width;
            this.height = height;
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const gl = canvas.getContext('webgl2', {
            premultipliedAlpha: false,
            preserveDrawingBuffer: false
        });
        if (!gl) throw new Error('WebGL2 not supported');
        this.gl = gl;
        this.canvas = canvas;
        this.width = width;
        this.height = height;
        this._setupQuad();
    }

    _setupQuad() {
        const gl = this.gl;
        const vao = gl.createVertexArray();
        gl.bindVertexArray(vao);
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1,  1, -1,  -1, 1,
            -1,  1,  1, -1,   1, 1,
        ]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.bindVertexArray(null);
        this.quadVAO = vao;
    }

    _compileProgram(vs, fs) {
        const gl = this.gl;
        const program = createProgram(gl, vs, fs);
        const uniforms = getUniformLocations(gl, program);
        return { program, uniforms };
    }

    _getProgram(effectId, shaderDef) {
        if (this.programs.has(effectId)) return this.programs.get(effectId);
        const compiled = this._compileProgram(VERTEX_SHADER, shaderDef.fragment);
        const entry = { program: compiled };
        this.programs.set(effectId, entry);
        return entry;
    }

    _createTexture(width, height, linearFilter) {
        const gl = this.gl;
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return tex;
    }

    _createFBO(texture) {
        const gl = this.gl;
        const fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return fbo;
    }

    _ensureBuffers(width, height, linearFilter) {
        const gl = this.gl;
        if (this.inputTexture) gl.deleteTexture(this.inputTexture);
        this.inputTexture = this._createTexture(width, height, linearFilter);
        if (this.fbTexture) gl.deleteTexture(this.fbTexture);
        this.fbTexture = this._createTexture(width, height, linearFilter);
        if (this.fbFBO) gl.deleteFramebuffer(this.fbFBO);
        this.fbFBO = this._createFBO(this.fbTexture);
    }

    _uploadImageData(imageData) {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.inputTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, imageData.data);
    }

    _drawToCanvas(program, uniforms, inputTex, targetFBO, linearFilter) {
        const gl = this.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, targetFBO);
        gl.viewport(0, 0, this.width, this.height);
        gl.useProgram(program.program);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, inputTex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, linearFilter ? gl.LINEAR : gl.NEAREST);
        gl.uniform1i(uniforms['u_texture'], 0);
        if (uniforms['u_flipY']) gl.uniform1f(uniforms['u_flipY'], 1.0);
        if (uniforms['u_resolution']) gl.uniform2f(uniforms['u_resolution'], this.width, this.height);
        if (uniforms['u_time']) gl.uniform1f(uniforms['u_time'], this._time || 0);
        gl.bindVertexArray(this.quadVAO);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        gl.bindVertexArray(null);
    }

    _readBack() {
        const gl = this.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbFBO);
        const pixels = new Uint8Array(this.width * this.height * 4);
        gl.readPixels(0, 0, this.width, this.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return pixels;
    }

    renderEffect(effectId, shaderDef, imageData, values) {
        const width = imageData.width;
        const height = imageData.height;

        this._ensureGL(width, height);
        this._ensureBuffers(width, height, shaderDef.linearFilter);
        this._uploadImageData(imageData);
        this._time = Math.random() * 100;  // random time for animated effects

        const entry = this._getProgram(effectId, shaderDef);
        const { program, uniforms } = entry;

        // Set effect-specific uniforms (bind program first!)
        const gl = this.gl;
        gl.useProgram(program.program);
        if (shaderDef.setUniforms) {
            shaderDef.setUniforms(gl, uniforms, values);
        }

        // Main pass: render to output FBO
        this._drawToCanvas(program, uniforms, this.inputTexture, this.fbFBO, shaderDef.linearFilter);

        // Read back result
        const pixels = this._readBack();
        return new ImageData(new Uint8ClampedArray(pixels), width, height);
    }
}

// Singleton renderer
var renderer = null;
function getRenderer() {
    if (!renderer) {
        renderer = new ErrorheadGLRenderer();
    }
    return renderer;
}

// ---- Bridge functions ----

// Build a params object from positional args, using the effect's param defaults.
function buildParams(effectDef, args) {
    const values = {};
    effectDef.params.forEach((param, i) => {
        let val;
        if (i < args.length && args[i] !== undefined && args[i] !== null) {
            val = args[i];
        } else {
            val = param.defaultValue;
        }
        if (param.type === 'range' || param.type === 'checkbox') {
            val = Number(val);
        } else if (param.type === 'select') {
            if (typeof val === 'number' || /^\d+$/.test(String(val))) {
                const idx = Math.round(Number(val));
                val = param.options[idx] ? param.options[idx].value : param.defaultValue;
            }
        }
        values[param.key] = val;
    });
    return values;
}

// Create a bridged function for a single errorhead effect.
// Returns a function (canvas, ctx, ...args) => Promise<void>.
function createBridge(effectId, effectDef, shaderDef) {
    return async (canvas, ctx, ...args) => {
        const width = canvas.width;
        const height = canvas.height;
        const imageData = ctx.getImageData(0, 0, width, height);
        const values = buildParams(effectDef, args);

        const r = getRenderer();
        const resultImageData = r.renderEffect(effectId, shaderDef, imageData, values);
        ctx.putImageData(resultImageData, 0, 0);
    };
}

// Build bridged functions for all errorhead effects.
function buildBridgedEffects() {
    const bridged = {};
    const defMap = new Map(EFFECT_DEFINITIONS.map(d => [d.id, d]));

    for (const [id, shaderDef] of Object.entries(EFFECT_SHADERS)) {
        const def = defMap.get(id);
        if (!def) continue;
        bridged[id] = createBridge(id, def, shaderDef);
    }
    return bridged;
}

// Build fx.js-compatible descriptors from all errorhead effects.
function buildFxDescriptors(bridgedEffects) {
    const descriptors = [];
    const defMap = new Map(EFFECT_DEFINITIONS.map(d => [d.id, d]));

    for (const [id, shaderDef] of Object.entries(EFFECT_SHADERS)) {
        const def = defMap.get(id);
        if (!def) continue;

        const inputs = def.params.map(param => {
            if (param.type === 'select') {
                const optionCount = param.options.length;
                return {
                    name: param.label || param.key,
                    type: 'knob',
                    min: 0,
                    max: optionCount - 1,
                    step: 1,
                    value: Math.max(0, param.options.findIndex(o => o.value === param.defaultValue))
                };
            }
            if (param.type === 'checkbox') {
                return {
                    name: param.label || param.key,
                    type: 'knob',
                    min: 0,
                    max: 1,
                    step: 1,
                    value: param.defaultValue
                };
            }
            // range type
            return {
                name: param.label || param.key,
                type: 'knob',
                min: param.min,
                max: param.max,
                step: param.step,
                value: param.defaultValue
            };
        });

        descriptors.push({
            name: 'eh-' + id,
            inputs,
            fn: bridgedEffects[id]
        });
    }
    return descriptors;
}

// Get list of errorhead effect IDs (for worker fxNameMap).
function getEffectIds() {
    return Object.keys(EFFECT_SHADERS);
}

module.exports = {
    buildBridgedEffects,
    buildFxDescriptors,
    createBridge,
    buildParams,
    getEffectIds
};
